import { Switch as MantineSwitch } from "@mantine/core";
import { componentWrapper } from "../../utilities";
import styles from "./switch.module.scss";
import type { CustomSwitchFactory, IProps, ISwitchFormikProps } from "./switch.types";
import { useField } from "formik";
import { kebabCase } from "lodash";

/**
 * Switch component for binary toggles.
 * @param {string} props.size - The size of the switch (default: "sm").
 * @returns {JSX.Element} The Switch component.
 */
const Switch = componentWrapper<CustomSwitchFactory, IProps>("Switch", ({ size = "sm", ...props }, ref) => {
    return (
        <MantineSwitch
            ref={ref}
            classNames={{
                root: styles["root"],
                track: styles["track"],
                thumb: styles["thumb"],
                input: styles["input"],
                body: styles["body"],
            }}
            size={size}
            withThumbIndicator={false}
            {...props}
        />
    );
});

/**
 * Switch component integrated with Formik for form handling and validation.
 * @param {string} props.name - The name of the switch field for Formik.
 * @param {function} props.validate - Custom validation function for the field.
 * @returns {JSX.Element} The SwitchFormik component.
 */
const SwitchFormik = componentWrapper<CustomSwitchFactory, ISwitchFormikProps>(
    "SwitchFormik",
    ({ name, validate, hideError, ...props }, ref) => {
        const [field, meta] = useField({ name, validate });
        return (
            <Switch
                ref={ref}
                data-testid={`switch-${kebabCase(name)}`}
                {...props}
                {...field}
                error={hideError ? undefined : meta.touched ? meta.error : undefined}
            />
        );
    }
);

Switch.Formik = SwitchFormik;

export { Switch, SwitchFormik };
